import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";

import PublicShell from "./PublicShell";
import { SITE } from "../../config/site";

function getProfileImage(user) {
  const rawProfileNumber =
    user?.profilePicture ??
    user?.pfpNumber ??
    user?.pfpIndex ??
    user?.avatarIndex ??
    null;

  const profileNumber = Number(rawProfileNumber);

  if (
    Number.isFinite(profileNumber) &&
    profileNumber >= 1 &&
    profileNumber <= 12
  ) {
    return `/images/pfp${profileNumber}.jpg`;
  }

  return (
    user?.profilePictureUrl ||
    user?.avatarUrl ||
    user?.imageUrl ||
    "/images/pfp1.jpg"
  );
}

export default function PublicUserPage() {
  const { username } = useParams();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadUser() {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(
          `/api/public/users/${encodeURIComponent(username || "")}`
        );

        if (cancelled) return;

        setUser(response?.data?.user || null);
      } catch (requestError) {
        if (cancelled) return;

        if (requestError?.response?.status === 404) {
          setError("This Veacon profile is unavailable.");
        } else {
          setError("Couldn’t load this profile right now.");
        }

        setUser(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadUser();

    return () => {
      cancelled = true;
    };
  }, [username]);

  const profilePic = useMemo(
    () => getProfileImage(user),
    [user]
  );

  if (loading) {
    return (
      <PublicShell>
        <section
          className="
            rounded-[2rem]
            border border-[rgb(var(--border))]
            bg-[rgb(var(--surface))]
            p-7 shadow-sm
            sm:p-9
          "
        >
          <div className="mx-auto max-w-md text-center">
            <div className="text-lg font-black">
              Loading profile…
            </div>

            <p className="mt-2 text-sm text-[rgb(var(--text-muted))]">
              Getting this Veacon profile.
            </p>
          </div>
        </section>
      </PublicShell>
    );
  }

  if (error || !user) {
    return (
      <PublicShell>
        <section
          className="
            rounded-[2rem]
            border border-[rgb(var(--border))]
            bg-[rgb(var(--surface))]
            p-7 text-center shadow-sm
            sm:p-10
          "
        >
          <div className="mx-auto max-w-md">
            <div className="text-2xl font-black tracking-tight">
              Profile unavailable
            </div>

            <p className="mt-3 text-sm leading-6 text-[rgb(var(--text-muted))] sm:text-base">
              {error || "This Veacon profile could not be found."}
            </p>

            <Link
              to="/"
              className="
                mt-6 inline-flex
                items-center justify-center
                rounded-2xl
                border border-[rgb(var(--border))]
                bg-[rgb(var(--surface))]
                px-5 py-3
                font-black
                transition
                hover:bg-[rgb(var(--elevated))]
              "
            >
              Explore Veacon
            </Link>
          </div>
        </section>
      </PublicShell>
    );
  }

  const safeUsername =
    typeof user?.username === "string" && user.username.trim()
      ? user.username.trim()
      : username || "user";

  const bio =
    typeof user?.bio === "string"
      ? user.bio.trim()
      : "";

  const appUrl =
    `veacon://u/${encodeURIComponent(safeUsername)}`;

  return (
    <PublicShell>
      <section
        className="
          relative overflow-hidden
          rounded-[2.25rem]
          border border-[rgb(var(--border))]
          bg-[rgb(var(--surface))]
          px-6 py-10
          shadow-[var(--shadow-soft)]
          sm:px-10 sm:py-14
        "
      >
        {/* Profile glow */}
        <div
          className="
            pointer-events-none absolute
            left-1/2 top-0
            h-72 w-72
            -translate-x-1/2 -translate-y-1/2
            rounded-full blur-3xl
          "
          style={{
            background:
              "radial-gradient(circle, rgb(var(--brand) / 0.16), transparent 68%)",
          }}
        />

        <div className="relative mx-auto flex max-w-xl flex-col items-center text-center">
          <div
            className="
              rounded-full p-[3px]
            "
            style={{
              background:
                "linear-gradient(135deg, rgb(var(--brand)), rgb(var(--cool)))",
            }}
          >
            <div className="rounded-full bg-[rgb(var(--surface))] p-[3px]">
              <img
                src={profilePic}
                alt={`@${safeUsername}`}
                className="
                  h-24 w-24
                  rounded-full
                  object-cover
                  sm:h-28 sm:w-28
                "
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/images/pfp1.jpg";
                }}
              />
            </div>
          </div>

          <div className="mt-6 text-xs font-black uppercase tracking-[0.18em] text-[rgb(var(--brand))]">
            Veacon profile
          </div>

          <h1 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
            @{safeUsername}
          </h1>

          {bio ? (
            <p className="mt-4 max-w-md text-sm leading-6 text-[rgb(var(--text-muted))] sm:text-base">
              {bio}
            </p>
          ) : null}

          <div
            className="
              mt-8 w-full
              rounded-[1.5rem]
              border border-[rgb(var(--border))]
              bg-[rgb(var(--elevated))]
              px-5 py-5
              sm:px-6
            "
          >
            <h2 className="text-lg font-black tracking-tight">
              Connect with @{safeUsername}
            </h2>

            <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[rgb(var(--text-muted))]">
              Open Veacon to view their profile and connect.
            </p>

            <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
              <a
                href={appUrl}
                className="
                  inline-flex min-h-12
                  items-center justify-center
                  rounded-2xl
                  bg-[rgb(var(--brand))]
                  px-6 py-3
                  font-black text-white
                  transition
                  hover:opacity-90
                "
              >
                Open in Veacon
              </a>

              <a
                href={SITE.appStoreUrl}
                target="_blank"
                rel="noreferrer"
                className="
                  inline-flex min-h-12
                  items-center justify-center
                  rounded-2xl
                  border border-[rgb(var(--border))]
                  bg-[rgb(var(--surface))]
                  px-6 py-3
                  font-black
                  transition-colors
                  hover:bg-[rgb(var(--bg-app))]
                "
              >
                Download Veacon
              </a>
            </div>
          </div>

          <Link
            to="/"
            className="
              mt-6 text-sm font-bold
              text-[rgb(var(--text-muted))]
              transition
              hover:text-[rgb(var(--text-main))]
            "
          >
            Learn more about Veacon →
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}